import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ContentUploader = ({ onClose, onUploadComplete }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'mandatory',
    type: 'course',
    format: 'video',
    level: 'beginner',
    duration: '',
    requiredFor: [],
    hasAssessment: false
  });

  const [files, setFiles] = useState([]);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e?.target?.files);
    setFiles(selectedFiles);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    console.log('Uploading content:', formData, files);
    onUploadComplete({ ...formData, files });
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Upload Training Content</h2>
            <p className="text-sm text-muted-foreground mt-1">Add new training materials to the repository</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Basic Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Content Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData?.title}
                  onChange={(e) => handleInputChange('title', e?.target?.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Safety Orientation Module"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description *
                </label>
                <textarea
                  required
                  value={formData?.description}
                  onChange={(e) => handleInputChange('description', e?.target?.value)}
                  rows={3}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  placeholder="Provide a detailed description of the training content..."
                />
              </div>
            </div>
          </div>

          {/* Classification */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Classification</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Category *</label>
                <select
                  value={formData?.category}
                  onChange={(e) => handleInputChange('category', e?.target?.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="mandatory">Mandatory Safety</option>
                  <option value="skill-based">Skill-Based Courses</option>
                  <option value="regulatory">Regulatory Compliance</option>
                  <option value="equipment">Equipment Training</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Content Type *</label>
                <select
                  value={formData?.type}
                  onChange={(e) => handleInputChange('type', e?.target?.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="course">Course</option>
                  <option value="certification">Certification</option>
                  <option value="reference">Reference Material</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Format *</label>
                <select
                  value={formData?.format}
                  onChange={(e) => handleInputChange('format', e?.target?.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="video">Video</option>
                  <option value="document">Document</option>
                  <option value="interactive">Interactive</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Level *</label>
                <select
                  value={formData?.level}
                  onChange={(e) => handleInputChange('level', e?.target?.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Duration *</label>
                <input
                  type="text"
                  required
                  value={formData?.duration}
                  onChange={(e) => handleInputChange('duration', e?.target?.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., 45 min"
                />
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Content Files</h3>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-foreground mb-2">
                Drag and drop files here, or click to browse
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Supported formats: MP4, PDF, PPTX, DOCX (Max 500MB)
              </p>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors cursor-pointer"
              >
                <Icon name="Upload" size={16} />
                Choose Files
              </label>
              {files?.length > 0 && (
                <div className="mt-4 space-y-2">
                  {files?.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted rounded text-left">
                      <span className="text-sm text-foreground">{file?.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {(file?.size / 1024 / 1024)?.toFixed(2)} MB
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Assessment Toggle */}
          <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
            <input
              type="checkbox"
              id="hasAssessment"
              checked={formData?.hasAssessment}
              onChange={(e) => handleInputChange('hasAssessment', e?.target?.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="hasAssessment" className="text-sm font-medium text-foreground">
              Include assessment/quiz with this training content
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
            >
              Upload Content
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContentUploader;